/**
 * Navigation latérale OPS : groupes repliables, recherche, état persistant.
 *
 * ── Pourquoi un état PERSISTANT ──────────────────────────────────────────
 * `AdminLayout` est rendu PAR CHAQUE PAGE (92 pages l'importent) : à chaque
 * clic dans le menu, l'ancienne page se démonte et la nouvelle monte un
 * AdminLayout neuf, donc un `<nav>` neuf dont le scroll repart à zéro et dont
 * les groupes repliés se rouvrent. C'était le « ça scrolle tout seul » du
 * fondateur (05/10/2026). Plutôt que de retoucher 92 pages, l'état du menu vit
 * HORS du composant :
 *   - groupes repliés  → localStorage, par utilisateur (survit au rechargement) ;
 *   - position de scroll → mémoire du module (survit aux remontages, pas au
 *     rechargement : au rechargement on cadre l'entrée active, une seule fois).
 * La recherche, elle, est volontairement remise à zéro à chaque page.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { filterNavItems, type StaffRole } from "@/lib/rbac";
import { resolveActivePath, type NavGroup } from "./navConfig";

const COLLAPSE_KEY = (userKey: string) => `ops.sidebar.collapsed.${userKey}`;

/** Scroll du menu, conservé entre deux montages (clé = utilisateur). */
const scrollMemory = new Map<string, number>();
/** Groupe de la dernière page visitée, pour détecter l'arrivée DANS un groupe. */
const activeGroupMemory = new Map<string, string | null>();

function readCollapsed(userKey: string): Record<string, boolean> {
  try {
    const raw = window.localStorage.getItem(COLLAPSE_KEY(userKey));
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeCollapsed(userKey: string, value: Record<string, boolean>) {
  try {
    window.localStorage.setItem(COLLAPSE_KEY(userKey), JSON.stringify(value));
  } catch {
    /* stockage indisponible (navigation privée) : l'état reste en mémoire */
  }
}

/** Recherche insensible à la casse ET aux accents (« reglages » trouve « Réglages »). */
function normalize(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

/** Vrai si l'élément est entièrement dans la zone visible du menu. */
function isWithinViewport(nav: HTMLElement, el: HTMLElement): boolean {
  const n = nav.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  return r.top >= n.top && r.bottom <= n.bottom;
}

function findNavItem(nav: HTMLElement, path: string): HTMLElement | null {
  return nav.querySelector<HTMLElement>(`[data-nav-path="${path.replace(/["\\]/g, "\\$&")}"]`);
}

export interface SidebarNavProps {
  groups: NavGroup[];
  role: StaffRole | undefined | null;
  /** Identifiant stable de l'utilisateur (e-mail ou id) pour les préférences. */
  userKey: string;
  pathname: string;
  searchQuery: string;
}

export function SidebarNav({ groups, role, userKey, pathname, searchQuery }: SidebarNavProps) {
  const navRef = useRef<HTMLElement>(null);

  const query = normalize(searchQuery);

  // RBAC d'abord, recherche ensuite ; un groupe vide disparaît.
  const visibleGroups = useMemo(
    () =>
      groups
        .map((group) => {
          let items = filterNavItems(role || null, group.items);
          if (query) items = items.filter((item) => normalize(item.label).includes(query));
          return { ...group, items };
        })
        .filter((group) => group.items.length > 0),
    [groups, role, query]
  );

  const activePath = useMemo(() => resolveActivePath(pathname, visibleGroups), [pathname, visibleGroups]);

  const activeGroupKey = useMemo(
    () => visibleGroups.find((g) => g.items.some((i) => i.path === activePath))?.key ?? null,
    [visibleGroups, activePath]
  );

  // ── Repli : un repli explicite GAGNE (replier le groupe courant le replie
  //    vraiment, et il reste replié sur les pages suivantes du même groupe).
  //    On ne rouvre un groupe replié que quand l'utilisateur arrive DANS ce
  //    groupe depuis l'extérieur : au montage (le composant est remonté par
  //    chaque page, d'où la mémoire de module) ou sur un changement de route
  //    sans remontage.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    const stored = readCollapsed(userKey);
    const prev = activeGroupMemory.get(userKey);
    activeGroupMemory.set(userKey, activeGroupKey);
    if (activeGroupKey && prev !== activeGroupKey && stored[activeGroupKey]) {
      const next = { ...stored, [activeGroupKey]: false };
      writeCollapsed(userKey, next);
      return next;
    }
    return stored;
  });
  useEffect(() => {
    const prev = activeGroupMemory.get(userKey);
    activeGroupMemory.set(userKey, activeGroupKey);
    if (prev === activeGroupKey || !activeGroupKey) return;
    setCollapsed((current) => {
      if (!current[activeGroupKey]) return current;
      const next = { ...current, [activeGroupKey]: false };
      writeCollapsed(userKey, next);
      return next;
    });
  }, [activeGroupKey, userKey]);

  // ── Scroll au montage : restaurer la position mémorisée, sinon cadrer
  //    l'entrée active UNE fois (block:"nearest" = aucun saut si elle est déjà
  //    visible). Jamais rejoué à chaque clic : c'est précisément ce qu'on corrige.
  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const saved = scrollMemory.get(userKey);
    if (saved !== undefined) {
      nav.scrollTop = saved;
    } else if (activePath) {
      findNavItem(nav, activePath)?.scrollIntoView?.({ block: "nearest" });
      scrollMemory.set(userKey, nav.scrollTop);
    }
    const onScroll = () => scrollMemory.set(userKey, nav.scrollTop);
    nav.addEventListener("scroll", onScroll, { passive: true });
    return () => nav.removeEventListener("scroll", onScroll);
    // Montage uniquement : l'entrée active ne doit pas déclencher de scroll ensuite.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userKey]);

  // ── Changement de route : si l'entrée active est HORS de la zone visible du
  //    menu (arrivée via Cmd+K, cloche, lien direct), on la ramène en
  //    block:"nearest" ; si elle est visible, on ne touche à rien.
  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav || !activePath) return;
    const el = findNavItem(nav, activePath);
    if (el && !isWithinViewport(nav, el)) {
      el.scrollIntoView?.({ block: "nearest" });
      scrollMemory.set(userKey, nav.scrollTop);
    }
  }, [activePath, userKey]);

  const toggleGroup = (key: string, headerEl: HTMLElement) => {
    setCollapsed((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      writeCollapsed(userKey, next);
      return next;
    });
    // Replier un groupe au-dessus du viewport ferait « sauter » le contenu :
    // on garde l'en-tête cliqué à l'écran.
    requestAnimationFrame(() => headerEl.scrollIntoView?.({ block: "nearest" }));
  };

  return (
    <nav ref={navRef} aria-label="Navigation principale" className="flex-1 overflow-y-auto px-2 py-2 space-y-1">
      {visibleGroups.map((group) => {
        const hasActive = group.key === activeGroupKey;
        // Un repli explicite gagne ; la recherche ouvre tout.
        const isCollapsed = Boolean(collapsed[group.key]) && !query;
        const listId = `nav-group-${group.key}`;
        return (
          <div key={group.key}>
            <button
              type="button"
              aria-expanded={!isCollapsed}
              aria-controls={isCollapsed ? undefined : listId}
              onClick={(e) => toggleGroup(group.key, e.currentTarget)}
              className={cn(
                "flex w-full items-center justify-between rounded-md px-2 py-1 text-[11px] font-semibold uppercase tracking-wider transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                hasActive ? "text-sidebar-accent-foreground" : "text-sidebar-foreground/55 hover:text-sidebar-foreground/80"
              )}
            >
              <span className="truncate">{group.label}</span>
              {isCollapsed ? <ChevronRight className="h-3 w-3 shrink-0" /> : <ChevronDown className="h-3 w-3 shrink-0" />}
            </button>
            {!isCollapsed && (
              <ul id={listId} className="mt-0.5 space-y-0.5">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = item.path === activePath;
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        data-nav-path={item.path}
                        aria-current={isActive ? "page" : undefined}
                        className={cn(
                          "flex items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-[13px] font-medium transition-colors",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                          isActive
                            ? "bg-sidebar-accent text-sidebar-accent-foreground"
                            : "text-sidebar-foreground hover:bg-sidebar-accent/50"
                        )}
                      >
                        <span className="flex min-w-0 items-center gap-2.5">
                          {Icon && <Icon className="h-4 w-4 shrink-0" />}
                          <span className="truncate">{item.label}</span>
                        </span>
                        {item.badge !== undefined && item.badge > 0 && (
                          <Badge variant="destructive" className="h-5 min-w-5 shrink-0 px-1.5 text-[11px]">
                            {item.badge > 99 ? "99+" : item.badge}
                          </Badge>
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
      {visibleGroups.length === 0 && (
        <p className="px-2 py-4 text-xs text-muted-foreground">Aucune entrée ne correspond.</p>
      )}
    </nav>
  );
}
